<script setup>
import { ref, inject, onMounted } from 'vue';
import { useProgramsComparisonStore } from '@/stores/programsComparison';
import { storeToRefs } from 'pinia';
import { router } from '@inertiajs/vue3';

const { search, destroy } = useProgramsComparisonStore();
const { all } = storeToRefs(useProgramsComparisonStore());

const params = ref({
    term: '',
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchProgramsComparison = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchProgramsComparison();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchProgramsComparison();
};

const openModal = (modal, mode = 'Create', comparison = null) => {
    modals.show(modal, {
        props: comparison ? {
            comparison: comparison,
            mode: mode,
        } : {
            mode: mode,
        }
    }).then(() => {
        searchProgramsComparison();
    });
};

const remove = async (comparison) => {
    const confirm = await modals.show('ConfirmationModal', {
        props: {
            title: 'Confirm Deletion',
            prompt: 'Are you sure you want to delete this program comparison?',
        },
    });

    if (confirm) {
        await destroy({ hash: comparison.hash });
        searchProgramsComparison();
    }
};

const visitProgramComparison = (comparison) => {
    router.visit(`/programs-comparisons/${comparison.hash}`);
};

onMounted(() => {
    searchProgramsComparison();
});
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.programs_comparisons') }}</template>
    <template #actions>
        <StyledButton @click="openModal('ProgramsComparisonsModal', 'Create')"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{  $t('label.new_comparison') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchProgramsComparison"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.status"
            clearable
            :placeholder="$t('label.status')"
            :options="[
                { label: $t('label.active'), value: true },
                { label: $t('label.inactive'), value: false },
            ]"
            name="status"
            @input="searchProgramsComparison"
            @click:clear="searchProgramsComparison" />
    </div>
    <template v-if="all?.data?.length">
        <DataTable
            :data="all"
            :headers="[
                { title: $t('label.primary_program_name'), key: 'primary_program_name', value: item => item?.primary_program?.program_name ?? '-', sortable: false },
                { title: $t('label.secondary_program_name'), key: 'secondary_program_name', value: item => item?.secondary_program?.program_name ?? '-', sortable: false },
                { title: $t('label.status'), key: 'status', value: item => item?.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
            ]"
            :actions="[
                { title: $t('label.edit'), icon: 'mdi-pencil', action: (item) => openModal('ProgramsComparisonsModal', 'Edit', item) },
                { title: $t('label.access'), icon: 'mdi-eye', action: (item) => visitProgramComparison(item) },
                { title: $t('label.delete'), icon: 'mdi-delete', action: (item) => remove(item) }
            ]"  
            @table-update="tableUpdate" />
    </template>
    <template v-else>
        <div class="text-center text-gray-500 py-8">{{ $t('label.no_programs_comparisons_found') }}</div>
    </template>
</MainContentLayout>
</template>
